// Missouri University of Science and Technology: public college page (#2652),
// official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Missouri S&T's Common Data Set 2025-2026 (the Excel file "CDS 2025-26
//    Gold for Posting.xlsx" linked from data.mst.edu/cds): C1 applicants,
//    admits and enrollees for Fall 2025 with the residency table (5
//    applicants and 1 admit of unknown residency are left out of the
//    residency rows); C8 test policy for Fall 2027; C14/C16 closing date and
//    rolling decisions; C21/C22 plans; G1/G5 costs.
//  - futurestudents.mst.edu, First-Time Freshmen (fall 2027 test-optional
//    review, scholarship deadlines, no application fee).
//  - College Scorecard, school id 178411 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Cost: the CDS G1/G5 figures, which its G section labels 2025-2026, the
// latest year Missouri S&T states. Student Financial Services' cost
// estimates page (sfs.mst.edu/tuitioncosts/cost-estimates) shows tuition by
// program group without naming a year, so it is not used. Tuition varies by
// program: the CDS says 68% of full-time undergraduates pay more than the
// G1 figure, which the cost note says.
// Quirks: the freshman page gives no final application deadline for fall
// 2027; the CDS closing date (July 1) and rolling notification are for the
// Fall 2026 cycle, so they are told as CDS facts in the early plans note.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'Missouri S&T Common Data Set 2025-2026',
        url: 'https://data.mst.edu/cds/',
        read: '2026-10-01',
    },
    freshmen: {
        label: 'Missouri S&T Admissions, First-Time Freshmen',
        url: 'https://futurestudents.mst.edu/admissions/first-timefreshmen/',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?178411-Missouri-University-of-Science-and-Technology',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'missouri-university-of-science-and-technology',
    kbId: 'missouri_university_of_science_and_technology',
    name: 'Missouri University of Science and Technology',
    shortName: 'Missouri S&T',
    aliases: ['S&T', 'UMR'],
    location: 'Rolla, MO',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Missouri S&T brand colors); decoration only.
    branding: {
        primary: '#154734',
        secondary: '#CEB888',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/missouri_university_of_science_and_technology_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 8330,
        admitted: 6550,
        enrolled: 1251,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 2966, admitted: 2585 },
            { group: 'Out-of-state', applied: 3373, admitted: 2845 },
            { group: 'International', applied: 1986, admitted: 1119 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Missouri S&T\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by college or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early plans',
        note: 'Missouri S&T has no early action plan and lists no early decision plan. Its Common Data Set reports that decisions go out on a rolling basis, with a July 1 application closing date for the fall 2026 class.',
        source: 'cds',
        where: 'sections C14, C16, C21 and C22',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Priority scholarship deadline', value: 'December 1' },
        rows: [
            { plan: 'Priority scholarship deadline', deadline: 'December 1', decision: null, binding: false },
            { plan: 'Final scholarship deadline', deadline: 'February 1', decision: null, binding: false },
        ],
        note: 'You can apply any time after six semesters of high school, and your application also counts for merit scholarships, so Missouri S&T says to apply early. There is no application fee. The freshman page names no final application deadline and no decision dates.',
        source: 'freshmen',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Freshman applicants for fall 2027 can be reviewed with or without ACT or SAT scores. Without scores, a committee decides and asks for more, including your activities and how you will contribute to Missouri S&T; scholarships may be more limited. With scores, you are evaluated on your courses, GPA or class rank, and the ACT or SAT, and S&T accepts superscores. With a core GPA below 3.2 you may be asked for more, such as letters of recommendation or test scores.',
        source: 'freshmen',
        cdsLine: 'The Common Data Set marks the SAT or ACT as recommended for fall 2027 applicants.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'freshmen',
    },

    cost: {
        year: '2025-2026',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition', amounts: [14328, 33516] },
            { item: 'Required fees', amounts: [1636, 1636] },
            { item: 'Food and housing (on campus)', amounts: [13422, 13422] },
            { item: 'Books and supplies', amounts: [720, 720] },
            { item: 'Transportation', amounts: [1774, 1774] },
            { item: 'Other expenses', amounts: [1172, 1172] },
        ],
        totals: [33052, 52240],
        note: 'For a full-time undergraduate living on campus, fall and spring, in 2025-2026, the latest year Missouri S&T\'s Common Data Set gives. Tuition varies by program: the CDS says 68% of full-time undergraduates pay more than the tuition shown.',
        source: 'cds',
        where: 'sections G1, G4 and G5',
        netPrice: {
            average: 15772,
            byIncome: [
                { income: '$0 to $30,000', amount: 11559 },
                { income: '$30,001 to $48,000', amount: 9759 },
                { income: '$48,001 to $75,000', amount: 11897 },
                { income: '$75,001 to $110,000', amount: 16680 },
                { income: 'Over $110,000', amount: 19665 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Missouri S&T\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
